import _ from 'lodash'
import { Instance, SnapshotOut, types } from 'mobx-state-tree'
import { withRootStore } from '../extensions'
import {
  ActiveGamesInfoModel,
  ActiveGamesInfoModelType,
  PaginatedGamesDetailsResponseModel,
  PaginatedGamesDetailsResponseModelType,
  PaginatedUserGamesModel,
  PaginatedUserGamesModelType
} from '../api-store'

/**
 * Dashboard store for managing dashboard-related data and operations
 */
export const DashboardStore = types
  .model('DashboardStore')
  .props({
    activeGamesInfo: types.optional(ActiveGamesInfoModel, {}),
    totalUsersCount: types.optional(types.number, 0),
    activeGamesDetails: types.optional(PaginatedGamesDetailsResponseModel, {}),
    userGames: types.optional(PaginatedUserGamesModel, {})
  })
  .extend(withRootStore)
  .views((self) => ({}))
  .actions((self) => ({
    // State setters
    setActiveGames(activeGamesInfo: ActiveGamesInfoModelType) {
      self.activeGamesInfo = activeGamesInfo
    },
    setTotalUsersCount(totalUsersCount: number) {
      self.totalUsersCount = totalUsersCount
    },
    setActiveGamesDetails(activeGamesDetails: PaginatedGamesDetailsResponseModelType) {
      self.activeGamesDetails = activeGamesDetails
    },
    setUserGames(userGames: PaginatedUserGamesModelType) {
      self.userGames = userGames
    }
  }))
  .actions((self) => ({
    // API interactions
    async getActiveGamesInfo() {
      try {
        const result = await self.rootStore.api.queryGetActiveGamesInfo(undefined, undefined, {
          fetchPolicy: 'no-cache'
        })
        if (result?.getActiveGamesInfo) {
          self.setActiveGames(result.getActiveGamesInfo)
        }

        return result
      } catch (e) {
        console.error('Failed to fetch active games info:', e)
        return null
      }
    },
    async getTotalUsersCount() {
      try {
        const result = await self.rootStore.api.queryGetTotalUsers(undefined, {
          fetchPolicy: 'no-cache'
        })
        if (result?.getTotalUsers) {
          self.setTotalUsersCount(result.getTotalUsers)
        }
        return result
      } catch (e) {
        console.error('Failed to fetch total users count:', e)
        return null
      }
    },
    async getAllGamesDetails({ page, pageSize }: { page: number; pageSize: number }) {
      try {
        const result = await self.rootStore.api.queryGetAllGamesDetails(
          { page, pageSize },
          (qb) =>
            qb
              .games(
                (g) => g.creator((c) => c.id.name).players((p) => p.id.name).createdAt.bots.events.gameId.gameType.hash
              )
              .pagination((p) => p.currentPage.pageSize.totalGames.totalPages),
          {
            fetchPolicy: 'no-cache'
          }
        )
        if (result?.getAllGamesDetails) {
          self.setActiveGamesDetails(result.getAllGamesDetails)
        }
        return result
      } catch (e) {
        console.error('Failed to fetch active games details:', e)
        return null
      }
    },
    async getUserGames({ userId, page, pageSize }: { userId: string; page: number; pageSize: number }) {
      try {
        const result = await self.rootStore.api.queryGetUserGames(
          { userId, page, pageSize },
          (qb) =>
            qb
              .games((g) => g.gameId.createdAt.hash.numberOfHands.numberOfHandsPlayed.numberOfHandsWon.timePlayed)
              .pagination((p) => p.currentPage.pageSize.totalGames.totalPages),
          {
            fetchPolicy: 'no-cache'
          }
        )
        if (result?.getUserGames) {
          self.setUserGames(result.getUserGames)
        }
        return result
      } catch (e) {
        console.error('Failed to fetch active games details:', e)
        return null
      }
    }
  }))

/**
 * The DashboardStore instance.
 */
export type DashboardStoreType = Instance<typeof DashboardStore>

/**
 * The data of a DashboardStore.
 */
export type DashboardStoreSnapshot = SnapshotOut<typeof DashboardStore>
